import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { Button, Flex } from '@mantine/core';
import React, { useContext, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Checkbox, Select, TextInput } from 'react-hook-form-mantine';
import { MantineDateInput } from '@dealstack/frontend/shared/components/mantineForm/MantineDateInput';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { EmploymentRecordRes, PersonType } from '@dealstack/shared/lib/generated';
import { CapManageControllerContext } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/CapManageEntityFlow.tsx';
import { EmploymentRecordFormValues } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EmploymentRecordDrawer/types.ts';
import { EmploymentRecordFormShape } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/EmploymentRecordDrawer/employmentRecord.zod.ts';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { makeOptionWithTestId } from '@dealstack/frontend/shared/components/Select/withOptionTestId';

// Employer display names are stable, so options are keyed by label.
const EmployerOption = makeOptionWithTestId(
	TEST_ELEMENT_IDS.CAP_EMPLOYMENT_RECORD_FORM_EMPLOYER_OPTION
);

interface Props {
	hasEditPermission: boolean;
	isRequestLoading: boolean;
	employmentRecord: EmploymentRecordRes | null;
	onSubmit: (formValues: EmploymentRecordFormValues, employmentRecordId?: string) => void;
}

const emptyFormValues: EmploymentRecordFormValues = {
	employerId: '',
	jobTitle: '',
	startDate: '',
	endDate: '',
	employeeReference: '',
	isPrimaryForEmployee: false,
};

export const EmploymentRecordForm = ({
	employmentRecord,
	hasEditPermission,
	onSubmit,
	isRequestLoading,
}: Props) => {
	const tCapManage = useTranslationCapManage();
	const { personsSelectOptions, selectedEntityId } = useContext(CapManageControllerContext);

	const employerOptions = useMemo(
		() =>
			personsSelectOptions
				.filter(
					(option) =>
						option.type === PersonType.LEGAL && option.value !== selectedEntityId
				)
				.map((option) => ({ value: option.value, label: option.label })),
		[personsSelectOptions, selectedEntityId]
	);

	const employmentForm = useForm<EmploymentRecordFormValues>({
		mode: 'all',
		resolver: zodResolver(EmploymentRecordFormShape(tCapManage)),
		defaultValues: emptyFormValues,
	});

	useEffect(() => {
		employmentForm.reset(
			employmentRecord
				? {
						employerId: employmentRecord.employerId,
						jobTitle: employmentRecord.jobTitle ?? '',
						startDate: employmentRecord.startDate ?? '',
						endDate: employmentRecord.endDate ?? '',
						employeeReference: employmentRecord.employeeReference ?? '',
						isPrimaryForEmployee: employmentRecord.isPrimaryForEmployee,
					}
				: emptyFormValues
		);
	}, [employmentRecord, employmentForm]);

	return (
		<FormProvider {...employmentForm}>
			<Flex direction="column" justify="space-between" gap="1rem" h="100%">
				<Flex direction="column" gap="1rem">
					<Select
						name="employerId"
						control={employmentForm.control}
						label={tCapManage('employment_record.drawer.employer.label')}
						placeholder={tCapManage('employment_record.drawer.employer.placeholder')}
						data={employerOptions}
						renderOption={EmployerOption}
						data-testid={TEST_ELEMENT_IDS.CAP_EMPLOYMENT_RECORD_FORM_EMPLOYER_SELECT}
						rightSection={<Icon variant="caret-down" size="lg" />}
						styles={{ rightSection: { pointerEvents: 'none' } }}
						searchable
						size="md"
						disabled={!hasEditPermission || !!employmentRecord}
					/>

					<TextInput
						name="jobTitle"
						control={employmentForm.control}
						label={tCapManage('employment_record.drawer.job_title.label')}
						placeholder={tCapManage('employment_record.drawer.job_title.placeholder')}
						data-testid={TEST_ELEMENT_IDS.CAP_EMPLOYMENT_RECORD_FORM_JOB_TITLE_INPUT}
						size="md"
						disabled={!hasEditPermission}
					/>

					<MantineDateInput
						name="startDate"
						label={tCapManage('employment_record.drawer.start_date.label')}
						data-testid={TEST_ELEMENT_IDS.CAP_EMPLOYMENT_RECORD_FORM_START_DATE_INPUT}
						size="md"
						maxDate={new Date()}
						clearable
						disabled={!hasEditPermission}
					/>

					<MantineDateInput
						name="endDate"
						label={tCapManage('employment_record.drawer.end_date.label')}
						data-testid={TEST_ELEMENT_IDS.CAP_EMPLOYMENT_RECORD_FORM_END_DATE_INPUT}
						size="md"
						maxDate={new Date()}
						clearable
						disabled={!hasEditPermission}
					/>

					<TextInput
						name="employeeReference"
						control={employmentForm.control}
						label={tCapManage('employment_record.drawer.employee_reference.label')}
						placeholder={tCapManage(
							'employment_record.drawer.employee_reference.placeholder'
						)}
						data-testid={
							TEST_ELEMENT_IDS.CAP_EMPLOYMENT_RECORD_FORM_EMPLOYEE_REFERENCE_INPUT
						}
						size="md"
						disabled={!hasEditPermission}
					/>

					<Checkbox
						name="isPrimaryForEmployee"
						control={employmentForm.control}
						label={tCapManage('employment_record.drawer.primary.label')}
						data-testid={TEST_ELEMENT_IDS.CAP_EMPLOYMENT_RECORD_FORM_PRIMARY_CHECKBOX}
						disabled={!hasEditPermission}
					/>
				</Flex>

				<Button
					onClick={employmentForm.handleSubmit((formValues) =>
						onSubmit(formValues, employmentRecord?.id)
					)}
					data-testid={TEST_ELEMENT_IDS.CAP_EMPLOYMENT_RECORD_FORM_SUBMIT_BUTTON}
					disabled={isRequestLoading || !hasEditPermission}
				>
					{employmentRecord
						? tCapManage('employment_record.drawer.edit_button')
						: tCapManage('employment_record.drawer.add_button')}
				</Button>
			</Flex>
		</FormProvider>
	);
};
